import React from 'react'
import { Avatar, Grid, Link, Typography } from "@mui/material";
import { Game, GamePlayer, isLoser, isWinner } from "model/Game"
import { User } from 'model/User';
import * as dateUtil from 'script/dateutil'
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from 'context/AuthContext';
import { useMediaContext } from 'context/MediaContext';

type Props = {
  isSubscriber: boolean
  isOwnPage: boolean
  user: User
  games: Game[]
}
export default function GameHistory(props: Props) {
  const { isPc } = useMediaContext()
  const navigate = useNavigate()
  const { auth } = useAuthContext()

  function isHost(game: Game): boolean {
    return props.user.uuid === game.host.uuid
  }

  function getUser(game: Game): GamePlayer {
    return isHost(game) ? game.host : game.guest
  }

  function getOpponent(game: Game): GamePlayer {
    return isHost(game) ? game.guest : game.host
  }

  function isWin(game: Game): boolean {
    return isWinner(props.user, game)
  }

  function isLose(game: Game): boolean {
    return isLoser(props.user, game)
  }

  function getWinCount(game: Game): number | string {
    if (!game.duels) return 'X'

    if (isWin(game)) return game.duels.filter(it => it.is_win).length
    else return game.duels!.filter(it => !it.is_win).length
  }

  function getLoseCount(game: Game): number | string {
    if (!game.duels) return 'X'

    return game.duels.length - Number(getWinCount(game))
  }

  function getDateStr(time: number): string {
    const date = new Date(time)
    return dateUtil.getString(date)
  }

  return (
    <>
      <Grid container sx={isPc ? { px: 2 } : {}}>
        {props.games.map((game, i) => (
          <React.Fragment key={game.id}>
            <Grid item xs={12} sx={{mt:1}}>
              <Typography variant='body2' color='text.secondary'>{getDateStr(game.start_time)}</Typography>
            </Grid>
            <Grid xs={2} md={1} item display='flex' alignItems='center'>
              <Avatar
                src={getUser(game).deck_type.thumbnail_url}
                variant='rounded'
                sx={isPc ? {width: 55, height: 55} : {width: 55, height: 55}}
              />
            </Grid>
            <Grid xs={8} md={3} item display='flex' flexDirection='column' alignItems='center' justifyContent='center'>
              <Typography variant='body2'>
                {getUser(game).name}
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                {getUser(game).rate}
              </Typography>
              <Typography variant='body2' color={getUser(game).rate_diff! > 0 ? 'error.dark' : 'primary.dark' }>
                {getUser(game).rate_diff! > 0 && '+'}{getUser(game).rate_diff}
              </Typography>
            </Grid>
            <Grid xs={2} md={1} item display='flex' alignItems='center' justifyContent='center'>
              {isWin(game) &&
                <Typography variant='h6' color='error.dark'>WIN</Typography>
              }
              {isLose(game) &&
                <Typography variant='h6' color='primary.dark'>LOSE</Typography>
              }
            </Grid>
            {isPc &&
              <Grid xs={0} md={2} item display='flex' alignItems='center' justifyContent='center'>
                <Typography variant={isPc ? 'h6' : 'body1'}>{getWinCount(game)} - {getLoseCount(game)}</Typography>
              </Grid>
            }
            {isPc ? (
              <Grid xs={2} md={1} item display='flex' alignItems='center' justifyContent='center'>
                {isLose(game) &&
                  <Typography variant='h6' color='error.dark'>WIN</Typography>
                }
                {isWin(game) &&
                  <Typography variant='h6' color='primary.dark'>LOSE</Typography>
                }
              </Grid>
            ) : (
              <Grid xs={2} md={1} item display='flex' alignItems='center'>
                <Avatar
                  src={getOpponent(game).deck_type.thumbnail_url}
                  variant='rounded'
                  sx={isPc ? {width: 55, height: 55} : {width: 55, height: 55}}
                />
              </Grid>
            )}
            <Grid xs={8} md={3} item display='flex' flexDirection='column' alignItems='center' justifyContent='center'>
              {(props.isOwnPage || getOpponent(game).open_game_history) &&
                <>
                  <Link
                    variant='body2'
                    onClick={() => navigate(`/mypage?id=${getOpponent(game).uuid}`)}
                    sx={{textDecoration: 'none', cursor: 'pointer'}}
                  >
                    {getOpponent(game).name}
                  </Link>
                  <Typography variant='body2' color='text.secondary'>
                    {getOpponent(game).rate}
                  </Typography>
                  <Typography variant='body2' color={getOpponent(game).rate_diff! > 0 ? 'error.dark' : 'primary.dark' }>
                    {getOpponent(game).rate_diff! > 0 && '+'}{getOpponent(game).rate_diff}
                  </Typography>
                </>
              }
              {!(props.isOwnPage || getOpponent(game).open_game_history) &&
                <>
                  <Typography variant='body2' color='text.secondary'>対戦履歴が非公開の</Typography>
                  <Typography variant='body2' color='text.secondary'>プレイヤー</Typography>
                </>
              }
            </Grid>
            {isPc ? (
              <Grid xs={2} md={1} item display='flex' alignItems='center'>
                <Avatar
                  src={getOpponent(game).deck_type.thumbnail_url}
                  variant='rounded'
                  sx={isPc ? {width: 55, height: 55} : {width: 55, height: 55}}
                />
              </Grid>
            ) : (
              <Grid xs={2} md={1} item display='flex' alignItems='center' justifyContent='center'>
                {isLose(game) &&
                  <Typography variant='h6' color='error.dark'>WIN</Typography>
                }
                {isWin(game) &&
                  <Typography variant='h6' color='primary.dark'>LOSE</Typography>
                }
              </Grid>
            )}
            {auth && auth.uid === props.user.uuid &&
              <Grid item xs={12} display='flex' flexDirection='row' justifyContent='flex-end'>
                <Link
                  variant='body2'
                  onClick={() => navigate(`/game?id=${game.id}`)}
                  sx={{cursor: 'pointer'}}
                >
                  ルームへ
                </Link>
              </Grid>
            }
          </React.Fragment>
        ))}
      </Grid>
    </>
  )
}